import { Button } from "@/components/button/Button";
import { useDialog } from "@/components/dialog/DialogContext";
import TableTab from "@/components/tabs/table-tab";
import { DEF_PG_SIZE_SUB_MIN } from "@/constants/maximum-page-size";
import { useLocale } from "@/contexts/localeContext";
import { TableContextProvider } from "@/contexts/table/TableContextProvider";
import { TranslationKeys } from "@/contexts/TranslationKeys";
import ExpiryReasonColumns from "@/models/ColumnDefinitions/ExpiryReasonColumns";
import { ListData } from "@/models/ListData";
import { ListQuery } from "@/models/ListQuery";
import { MasterExpiry } from "@/models/MasterExpiry";
import { PaginationModel } from "@/models/Pagination";
import ExpiryReasonSearch from "@/models/search/ExpiryReasonSearch";
import { assignUnassignMasterExpiry, editMasterExpiry, getExpiryReasons, getMasterExpiryById, getMasterExpiryForPerson } from "@/services/expiryReasonService";
import { getErrorMessage, showLoadItemError } from "@/services/utilities/ServiceError";
import { appendFilter } from "@/utilities/filters";
import { FC, useEffect, useState } from "react";
import { toast } from "react-toastify";
import ExpiryReasonsModal from "../../expiry-reasons/Assign/ExpiryReasonModal";
import MasterExpiryModal from "../../expiry-reasons/Assign/EditMasterExpiryModal";
import { removeColumns } from "@/models/utilities/columnHelper";
import { useSelector } from "react-redux";
import { IAppState } from "@/store/types";
import { USER_CAPABILITIES_EXPIRY_REASON } from "@/constants/user-capabilities-constants";
import useUserCapability from "@/hooks/useUserCapability";

interface AssignedMasterExpiryProps {
    masterId: string;
    setBusy: React.Dispatch<React.SetStateAction<boolean>>
    dataReloadMethod?: () => void
}

const AssignedExpiryReasonsTab: FC<AssignedMasterExpiryProps> = ({ masterId, setBusy, dataReloadMethod }) => {
    const { translate } = useLocale();
    const dialog = useDialog();
    const [showManageModal, setShowManageModal] = useState<boolean>(false);
    const [showEditMasterExpiry, setShowEditMasterExpiry] = useState<boolean>(false);
    const [masterExpiry, setMasterExpiry] = useState<MasterExpiry | undefined>(undefined);
    const [dataRefresh, setDataRefresh] = useState(() => (dataMethod?: (query?: ListQuery) => Promise<ListData>) => { return });
    const hasExpiryReasonPermission = useUserCapability(USER_CAPABILITIES_EXPIRY_REASON);
    const user = useSelector((state: IAppState) => state.userInfo);

    const manualDataRefresh = (refreshData: (dataMethod?: (query?: ListQuery) => Promise<ListData>) => void) => {
        setDataRefresh(() => refreshData);
    }

    const onEdit = async (id: string) => {
        try {
            const masterExpiry: MasterExpiry = await getMasterExpiryById(id);
            setMasterExpiry(masterExpiry);
            setShowEditMasterExpiry(true);
        } catch (error) {
            showLoadItemError(error, dialog, translate, () => { setShowEditMasterExpiry(false) });
        }
    }

    const getAssignedItems = async (setSelectedIds: React.Dispatch<React.SetStateAction<string[]>>, setSelectedRows: React.Dispatch<React.SetStateAction<any[]>>, setOriginalSelectedIds: React.Dispatch<React.SetStateAction<string[]>>, setOriginalSelectedRows: React.Dispatch<React.SetStateAction<any[]>>) => {
        try {
            let pageNumber = 1, pagination: PaginationModel, ids: string[] = [], rows: any[] = []
            do {
                const expiryReasons = await getMasterExpiryForPerson(masterId, { pageSize: 100, goToPage: pageNumber });
                ids = ids.concat(expiryReasons.rows.map(expiryReason => expiryReason.expiryReasonId.toString()));
                rows = rows.concat(expiryReasons.rows);
                pagination = expiryReasons.pagination;
                pageNumber++;
            } while (pageNumber <= pagination.totalPages);
            setSelectedIds(ids);
            setSelectedRows(rows)
            setOriginalSelectedIds(ids);
            setOriginalSelectedRows(rows);
        } catch (error) {
            toast.warn(getErrorMessage(error));
        }
    }

    const onMasterExpiryEdit = async (values: MasterExpiry) => {
        setBusy(true);
        try {
            await editMasterExpiry(values);
            toast.success(translate.byKey(TranslationKeys.success), {toastId: "toastMessage"});
            setShowEditMasterExpiry(false);
            dataRefresh();
        } catch (error) {
            dialog.pushError({ title: translate.byKey(TranslationKeys.error), message: getErrorMessage(error) });
        } finally {
            setBusy(false);
        }
    };

    const handleSubmit = async (toBeAssigned: string[], toBeAssignedRows: any[], toBeUnassigned: string[], values: any) => {
        setBusy(true)
        try {
            await assignUnassignMasterExpiry(toBeAssignedRows, toBeUnassigned, masterId, values);
            toast.success(translate.byKey(TranslationKeys.success), {toastId: "toastMessage"});
            setShowManageModal(false);
            dataRefresh();
            dataReloadMethod?.();
        } catch (error) {
            dialog.pushError({title: translate.byKey(TranslationKeys.error), message: getErrorMessage(error)});
        } finally {
            setBusy(false);
        }
    }

    const getDataMethod = (listParameters?: ListQuery) => {
        return getMasterExpiryForPerson(masterId, { ...listParameters, filters: appendFilter(masterId, "Master", "master.id", listParameters?.filters ?? []) })
    }

    useEffect(() =>{
        dataRefresh(getDataMethod);
    },[masterId])

    return <>
        <TableContextProvider
            dataMethod={(listParameters?: ListQuery) => getDataMethod(listParameters)}
            manualDataRefresh={manualDataRefresh}
            tableDescriptor={removeColumns(ExpiryReasonColumns, ['eventExpiry', 'eventWarning', 'totalMasterExpiries'])}
            searchDescriptor={ExpiryReasonSearch}
            useTable
            usePagination
            useSort
            useSearch
            useFilter
            maxPageSize={DEF_PG_SIZE_SUB_MIN}
            editMethod={hasExpiryReasonPermission ? onEdit : undefined}
            canExportReport={false} >
            <TableTab
                title={translate.byKey(TranslationKeys.assignedOperationCompliance)}
                selectRows={false}
                cyName="assignedOperationCompliance">
                {hasExpiryReasonPermission && <Button variant="submit" shape="rounded" id="manageButton" className="py-1 px-5" onClick={() => setShowManageModal(true)}>{translate.byKey(TranslationKeys.manage)}</Button>}
            </TableTab>
        </TableContextProvider>
        <ExpiryReasonsModal
            assigningType="EXPIRY_REASONS"
            dataMethod={(listParameters?: ListQuery) => getExpiryReasons(listParameters)}
            tableColumns={removeColumns(ExpiryReasonColumns, ['eventExpiry', 'eventWarning', 'masterExpiryStartDate', 'masterExpiryEndDate', 'totalMasterExpiries'])}
            searchColumns={ExpiryReasonSearch}
            modalTitle={translate.byKey(TranslationKeys.assignComplianceCondition)}
            assigningTitle={translate.byKey(TranslationKeys.complianceCondition)}
            isOpen={showManageModal}
            onClose={() => setShowManageModal(false)}
            getAssignedItems={getAssignedItems}
            handleSubmit={handleSubmit} 
            idPath="expiryReasonId"/>
        <MasterExpiryModal
            assigningType="EXPIRY_REASONS"
            isOpen={showEditMasterExpiry}
            onClose={() => setShowEditMasterExpiry(false)}
            masterExpiry={masterExpiry}
            onSave={onMasterExpiryEdit} />
    </>
}

export default AssignedExpiryReasonsTab;
